import React, { useEffect } from "react";
import { getSplitTreatmentsInGetInitialProps } from "@leafly-com/split-next";
import { track } from "@leafly-com/user-history-utils";
import { NextPage, NextPageContext } from "next";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useSelector } from "react-redux";
import { JsonLd } from "react-schemaorg";
import { LocalBusiness } from "schema-dts";

import getRegionComplianceRules from "api/requests/consumerApi/getRegionComplianceRules";
import { getDispensary } from "api/requests/getDispensary";
import getMenuItems, { GetMenuItemsReturn } from "api/requests/getMenuItems";
import { DISPENSARY_PAGES } from "constants/dispensary";
import { MenuProvider } from "context/MenuContext";
import { Dispensary } from "custom-types/Dispensary";
import useDomain from "hooks/useDomain";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import { dispensaryJsonSchema } from "lib/dispensaryMetadata";
import { COMPLIANCE_RULESET_LOAD } from "redux/reducers/complianceRules";
import {
  getIsOrderingAllowed,
  getIsThirdPartyOrderingProhibited,
} from "redux/selectors/complianceRules";
import { getDomainCountryCode } from "redux/selectors/config";
import { getCoordinates } from "redux/selectors/location";
import { AppStore } from "redux/store";
import { getDispensaryUrl } from "utils/dispensaryUrls";
import logError from "utils/logError";
import { getMenuTypeCookie, includesDualMenuTag } from "utils/menuTypeUtils";
import { shouldNoIndex } from "utils/shouldNoIndex";
import { getSplitKey } from "utils/split/getSplitKey";
import useTrackDispensaryPageViewSplitEvent from "utils/split/useTrackDispensaryPageViewSplitEvent";
import { validateDispensary } from "utils/validateDispensary";

import AboutDispensary from "components/Dispensary/AboutDispensary";
import DispensaryBreadcrumbs from "components/Dispensary/DispensaryBreadcrumbs";
import DispensaryHead from "components/Dispensary/DispensaryHead";
import DispensaryHeader from "components/Dispensary/DispensaryHeader";
import DispensaryTabs from "components/Dispensary/DispensaryTabs";
import NonCompliantMenu from "components/Dispensary/NonCompliantMenu";
import ReviewConfirmationModal from "components/Dispensary/ReviewsHeader/ReviewConfirmationModal";
import DispensaryPixelTracking from "components/DispensaryPixelTracking/DispensaryPixelTracking";
import UberEgress from "components/UberEgress";

const PaidTierMenu = dynamic(
  () => import("components/Dispensary/PaidTierMenu")
);
const MenuNotAvailable = dynamic(
  () => import("components/Dispensary/MenuNotAvailable")
);
const NearbyStores = dynamic(
  () => import("components/Dispensary/NearbyStores")
);

type MainPageProps = {
  dispensary: Dispensary;
  menuData: GetMenuItemsReturn;
  dispensaryHeaderDesignUpdates?: boolean;
  showNewCoinListExperience?: boolean;
};

const MainPage: NextPage<MainPageProps> = ({
  dispensary,
  menuData,
  dispensaryHeaderDesignUpdates,
  showNewCoinListExperience,
}) => {
  const { id, featureTier, retailType, slug, uberEatsStoreUrl, hasMenuItems } =
    dispensary;
  const baseUrl = useDomain();

  const isInfoTier = featureTier === "info";
  const isDispensaryRetailType = retailType === "dispensary";

  useTrackDispensaryPageViewSplitEvent({ page: "main", tier: featureTier });

  useEffect(() => {
    track({
      resourceId: id,
      slug,
      type: retailType as
        | "dispensary"
        | "brand"
        | "strain"
        | "product"
        | "news-article"
        | "menu-item",
    });
  }, [retailType, id]);

  const router = useRouter();
  const { asPath } = router;
  const countryCode = useDomainCountryCode();
  const thirdPartyOrderingProhibited = useSelector(
    getIsThirdPartyOrderingProhibited
  );

  let isLeaflyOrderingAllowed = useSelector(getIsOrderingAllowed);
  // * Only "dispensary" types are restricted, since clinic and cbd-store types
  // * typically don't sell restricted items.
  if (!isDispensaryRetailType) {
    isLeaflyOrderingAllowed = true;
  }

  const showConfirmationModal = router?.query?.show_confirmation_modal;
  const showFavoriteDispensaryMessage = router?.query?.show_favorite_message;
  const ssoMenuRedirect = async () => {
    try {
      await router.push(
        getDispensaryUrl({
          countryCode,
          slug,
          tab: "menu",
        })
      );
    } catch (err) {
      logError(
        "Error redirecting in ssoMenuRedirect on /dispensary-info/[slug]"
      );
    }
  };

  const ssoDispensaryRedirect = async () => {
    try {
      await router.push(
        getDispensaryUrl({
          countryCode,
          slug,
        })
      );
    } catch (err) {
      logError(
        "Error redirecting in ssoDispensaryRedirect on /dispensary-info/[slug]"
      );
    }
  };

  return (
    <MenuProvider initialData={menuData}>
      {dispensary.state && (
        <DispensaryPixelTracking dispensaryState={dispensary.state} />
      )}
      <DispensaryHead
        dispensary={dispensary}
        pageType="main"
        noIndex={shouldNoIndex(asPath)}
      />
      <DispensaryHeader
        isInfoTier={isInfoTier}
        dispensaryHeaderDesignUpdates={dispensaryHeaderDesignUpdates}
      />
      <JsonLd<LocalBusiness>
        item={dispensaryJsonSchema(dispensary, baseUrl, dispensary.country)}
      />
      {thirdPartyOrderingProhibited && isDispensaryRetailType && (
        <NonCompliantMenu
          className="container mb-xl"
          hideCTA={isInfoTier}
          thirdPartyOrderingProhibited={thirdPartyOrderingProhibited}
          isLeaflyOrderingAllowed={isLeaflyOrderingAllowed}
        />
      )}
      <DispensaryTabs
        activeTab={DISPENSARY_PAGES.main}
        isInfoTier={isInfoTier}
      />
      {uberEatsStoreUrl && (
        <UberEgress
          url={uberEatsStoreUrl}
          className="container mt-lg md:hidden"
          dispensaryId={dispensary.id}
        />
      )}
      <>
        {!isInfoTier && hasMenuItems ? (
          <PaidTierMenu showNewCoinListExperience={showNewCoinListExperience} />
        ) : (
          <>
            <MenuNotAvailable className="container my-section" />
            {isInfoTier && (
              <NearbyStores className="container mt-xl md:mt-[40px] mb-lg" />
            )}
          </>
        )}
      </>

      <AboutDispensary />

      {showConfirmationModal && (
        <ReviewConfirmationModal
          favorite={showFavoriteDispensaryMessage}
          ssoMenuRedirect={ssoMenuRedirect}
          ssoDispensaryRedirect={ssoDispensaryRedirect}
        />
      )}
      <div className="mb-[104px]">
        <DispensaryBreadcrumbs />
      </div>
    </MenuProvider>
  );
};

MainPage.getInitialProps = async (context: NextPageContext<AppStore>) => {
  const { req, res, query, store } = context;
  const { slug, menu_type } = query;

  const filters = query || {};

  const coordinates = getCoordinates(store.getState());
  const countryCode = getDomainCountryCode(store.getState());

  try {
    const dispensary = await getDispensary(slug as string, coordinates);

    const {
      "NL-894_coinListNewExperience_flag": { treatment: coinListNewExperience },
    } = await getSplitTreatmentsInGetInitialProps(
      "NL-894_coinListNewExperience_flag",
      getSplitKey(context)
    );

    const showNewCoinListExperience = coinListNewExperience === "on";

    const { redirect } = await validateDispensary({
      countryCode,
      dispensary,
      query,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- temporarily using non-null assertion before refactoring validateDispensary
      req: req!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- temporarily using non-null assertion before refactoring validateDispensary
      res: res!,
    });

    if (redirect) {
      return;
    }

    const userMedRecPreference = menu_type || getMenuTypeCookie(req);
    const isDualLicenseDispensary = includesDualMenuTag(dispensary?.tags || []);

    store.dispatch({
      dispensary,
      type: "dispensary/load",
    });

    const menuItemsCall = getMenuItems(slug as string, {
      ...filters,
      enableNewFilters: true,
      menu_type: isDualLicenseDispensary ? userMedRecPreference : undefined,
    });

    const complianceCall = getRegionComplianceRules(
      dispensary?.state,
      dispensary?.country
    );

    const [menuData, complianceRuleset] = await Promise.all([
      menuItemsCall,
      complianceCall,
    ]);

    const {
      "NL-893_dispensaryHeaderBusinessInfo_test": {
        treatment: dispensaryHeaderTest,
      },
    } = await getSplitTreatmentsInGetInitialProps(
      "NL-893_dispensaryHeaderBusinessInfo_test",
      getSplitKey(context)
    );

    const dispensaryHeaderDesignUpdates = dispensaryHeaderTest === "on";

    if (complianceRuleset) {
      store.dispatch({
        complianceRuleset,
        type: COMPLIANCE_RULESET_LOAD,
      });
    }

    return {
      dispensary,
      dispensaryHeaderDesignUpdates,
      menuData,
      showNewCoinListExperience,
    };
  } catch (err) {
    logError(err.message || "Unknown error", {
      functionName: "/dispensary-info/[slug] getInitialProps",
    });

    return err;
  }
};

export default MainPage;
